/**
 * silenceAndPauseDetector.js
 *
 * Detects when a minimal, quiet, or near-silent response is more appropriate
 * than a full answer. Gives Kevia permission to be brief.
 *
 * A wise pastor does not fill every moment with words.
 * Sometimes presence, acknowledgment, and a single phrase is the
 * wisest pastoral response.
 */

const SILENCE_SIGNALS = [
  /acabo\s+de\s+(?:perder|enterarme|saber)/i,
  /solo\s+quería\s+(?:contarte|decirte|compartirlo|hablar)/i,
  /no\s+(?:sé|se)\s+(?:qué\s+decir|qué\s+hacer|cómo\s+procesar|para\s+qué)/i,
  /estoy\s+(?:muy\s+)?(?:mal|roto|destrozado|devastado|quebrado)/i,
  /no\s+puedo\s+(?:más|hablar|pensar|seguir)/i,
  /necesito\s+(?:un\s+momento|tiempo|espacio)/i,
  /me\s+siento\s+(?:perdido|vacío|sin\s+fuerzas|sin\s+esperanza)/i,
];

/**
 * @param {{
 *   text:   string,
 *   emotion: object|null,
 *   depth:   string,
 *   timing:  string,
 * }} params
 * @returns {{ silenceAppropriate: boolean, hint: string|null }}
 */
export function detectSilenceAppropriate({ text, emotion, depth, timing }) {
  // Already decided to hold or micro depth → silence is the response
  if (depth === "micro" || timing === "hold") {
    return {
      silenceAppropriate: true,
      hint: "Una frase breve de presencia es suficiente. No expliques, no analices, no resuelvas.",
    };
  }

  // Text signals that the person just needed to express, not receive an answer
  const hasTextSignal = SILENCE_SIGNALS.some((p) => p.test(text ?? ""));
  if (hasTextSignal) {
    return {
      silenceAppropriate: true,
      hint: "Menos es más. Una o dos frases de acompañamiento genuino superan cualquier análisis.",
    };
  }

  // Acute grief without a question → likely sharing, not asking
  if (
    emotion?.dominantEmotion === "grief" &&
    emotion?.intensity === "high" &&
    !/(¿|\?|cómo|qué|cuándo|dónde|por qué|puedes|podrías)/i.test(text ?? "")
  ) {
    return {
      silenceAppropriate: true,
      hint: "No hay pregunta aquí. Solo acompaña con presencia. Una frase cálida es suficiente.",
    };
  }

  return { silenceAppropriate: false, hint: null };
}
